import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import logo from '../assets/logo.webp';
import gem from '../assets/gem.webp';
import { DOT_LEFT, trackScale } from '../campaign';
import { Crop, BgRot, GoldButton, fitScene } from './Shared';

/* ------------------------------------------------------------------
   "How to play" story, Instagram style. An "Introducing" title card
   (2.55s) leads into two auto-advancing slides (8s): the demo track
   ("The Adventure Starts Now"), then the Gold Chest — a headline + chest
   drop for 3s that gives way to the prize spread, the launch date and
   the Start CTA. Tap right → next, tap left → previous, press & hold →
   pause, X → skip. The last slide waits for the CTA (or a tap) and then
   calls onEnd. Laid out on the 375px stage; the parent sizes/zooms it.
   ------------------------------------------------------------------ */

const INTRO_MS = 2550;
const DUR = 8000;
const LAST = 1;
const OUT_MS = 460;
const PHASE_A_MS = 3000;  // headline + chest
const PHASE_OUT_MS = 560; // its blur-out before the prizes
const DEMO0 = { step: 0, line: 0, pop: 0, chest: false, flip: 0 };
/* slide 2's phase classes; the blur-out keeps `pa` so the chest doesn't snap away under it */
const PHASE_CLASS = { pa: 'gq-pa', 'pa-out': 'gq-pa gq-pa-out', pb: 'gq-pb' };
const EQ_LINES = ['Every ₹500 = 1 Gem', '₹1,000 = 2 Gems', '₹1,500 = 3 Gems', '₹2,000 = 4 Gems + a Gold Chest'];
/* the slide content block: top:104 → prize art bottom 613 on the design; it
   may rise to 80 (under the header) and must clear the bottom-anchored launch
   line + CTA (812 − 696 + a 16px gap) — see fitScene() in Shared.js */
const SCENE = { designTop: 104, height: 509, minTop: 80, bottom: 132 };

/* 12 twinkling specks, randomised once per mount */
function makeBokeh() {
  return Array.from({ length: 12 }, (_, i) => {
    const sz = +(2 + Math.random() * 3).toFixed(1);
    const col = Math.random() < 0.55 ? '#F6D5AC' : (Math.random() < 0.5 ? '#DCC4FF' : '#FBCC65');
    return {
      id: i,
      style: {
        left: `${(Math.random() * 100).toFixed(1)}%`,
        top: `${(8 + Math.random() * 88).toFixed(1)}%`,
        width: sz,
        height: sz,
        background: col,
        boxShadow: `0 0 ${sz * 2}px ${col}`,
        '--d': `${(2.6 + Math.random() * 3.4).toFixed(2)}s`,
        '--dl': `${(-Math.random() * 6).toFixed(2)}s`,
      },
    };
  });
}

/* setTimeout that is cleared on unmount */
function useTimers() {
  const ids = useRef(new Set());
  const set = useCallback((fn, ms) => {
    const id = setTimeout(() => { ids.current.delete(id); fn(); }, ms);
    ids.current.add(id);
    return id;
  }, []);
  const clear = useCallback((id) => { clearTimeout(id); ids.current.delete(id); }, []);
  useEffect(() => {
    const pending = ids.current;
    return () => pending.forEach(clearTimeout);
  }, []);
  return { set, clear };
}

/* Invisible tap target. A quick tap fires onTap; holding for 220ms fires
   onHold and the release fires onRelease instead of onTap. */
function TapZone({ side, onTap, onHold, onRelease }) {
  const held = useRef(false);
  const holdT = useRef(0);
  useEffect(() => () => clearTimeout(holdT.current), []);

  const down = () => {
    held.current = false;
    clearTimeout(holdT.current);
    holdT.current = setTimeout(() => { held.current = true; onHold(); }, 220);
  };
  const up = () => {
    clearTimeout(holdT.current);
    if (held.current) { held.current = false; onRelease(); return; }
    onTap();
  };
  const cancel = () => {
    clearTimeout(holdT.current);
    if (held.current) { held.current = false; onRelease(); }
  };
  return (
    <div
      className={`gq-tap gq-${side}`}
      onPointerDown={down}
      onPointerUp={up}
      onPointerCancel={cancel}
      onPointerLeave={cancel}
    />
  );
}

/* title card — "Introducing" + the logo, with a burst and a light sweep */
function Intro({ stageHeight }) {
  /* centred on the stage the way it sits on the 812px design (eyebrow at 300) */
  const top = Math.round(stageHeight / 2 - 106);
  return (
    <div className="gq-intro gq-play" aria-hidden="true">
      <div className="gq-iscene" style={{ top }}>
        <div className="gq-ieyebrow">Introducing</div>
        <div className="gq-iburst" />
        <Crop kind="logo" className="gq-ilogo" alt="The Golden Quest" />
        <div className="gq-isweep" />
      </div>
    </div>
  );
}

/* slide 1 — the logo, the promise and a live demo of the Gem track */
function SlideStart({ demo, scene }) {
  const step = demo.step;
  return (
    <div className="gq-scene" style={scene}>
      <Crop kind="logo" className="gq-s-logo gq-a-logo" alt="The Golden Quest" />
      <div className="gq-copy gq-top">
        <div className="gq-h gq-a-h">The Adventure Starts Now</div>
        <div className="gq-p gq-a-p">Complete tasks to earn Gems, open Gems and win Bitcoin or Gold prizes.</div>
      </div>
      <div className="gq-demo gq-a-demo">
        <div className="gq-track">
          <div className="gq-fill" style={{ transform: `scaleX(${trackScale(step)})` }} />
          <div className="gq-dot" style={{ transform: `translateX(${DOT_LEFT[step] - 2}px)` }} />
        </div>
        {[1, 2, 3, 4].map((k) => (
          <Crop
            key={k}
            kind="gem"
            className={`gq-gem gq-g${k}${k <= step ? ' gq-on' : ''}${demo.pop === k ? ' gq-pop' : ''}`}
          />
        ))}
        <div className={`gq-chest${demo.chest ? ' gq-earned' : ''}`}>
          <Crop kind="chest" className="gq-cglow" />
          <Crop kind="chest" />
        </div>
        {['₹0', '₹500', '₹1000', '₹1500', '₹2000'].map((label, k) => (
          <div key={label} className={`gq-t gq-lab gq-l${k}`}>{label}</div>
        ))}
        <div className="gq-eq">
          {/* re-keyed so the flip animation replays on every line change */}
          <span key={demo.flip} className={demo.flip ? 'gq-flip' : ''}>{EQ_LINES[demo.line]}</span>
        </div>
      </div>
    </div>
  );
}

/* slide 2 — phase A: "Complete a streak to get a Gold Chest" + the chest;
   phase B: the prize spread, the draw copy, the launch date and the CTA */
function SlideChest({ drawLabel, startsLabel, ctaLabel, scene, onCta }) {
  return (
    <>
      <div className="gq-phaseA">
        <div className="gq-scene" style={scene}>
          <div className="gq-copy gq-top">
            <div className="gq-h gq-a-h">Complete a streak to get a Gold Chest</div>
          </div>
          <div className="gq-aglow" />
          <Crop kind="chest" className="gq-achest" />
        </div>
      </div>
      <div className="gq-phaseB">
        <div className="gq-scene" style={scene}>
          <div className="gq-pbimgs">
            <Crop kind="ps5" className="gq-pi gq-pi1 gq-rot" />
            <Crop kind="iphone" className="gq-pi gq-pi2" />
            <Crop kind="watch" className="gq-pi gq-pi3 gq-rot" />
            <Crop kind="watch" className="gq-pi gq-pi4 gq-rot" />
            <Crop kind="ps5" className="gq-pi gq-pi5 gq-rot" />
            <Crop kind="iphone" className="gq-pi gq-pi6 gq-rot" />
          </div>
          <div className="gq-copy gq-sub">
            <div className="gq-p">
              Open your Gold Chests on {drawLabel} for mega prizes like an iPhone Duo, PlayStation 5, Apple Watch, Apple AirPods, Bitcoin, Gold and so much more!
            </div>
          </div>
        </div>
        {startsLabel && <div className="gq-starts">{startsLabel}</div>}
        <GoldButton onClick={onCta}>{ctaLabel}</GoldButton>
      </div>
    </>
  );
}

export default function Story({
  ctaLabel = 'Start', drawLabel = 'Nov 13th', startsLabel = 'Starts on 18th October',
  zoom = 1, stageHeight = 812, onEnd,
}) {
  const [intro, setIntro] = useState(true);
  const [slide, setSlide] = useState(-1);          // -1 while the title card plays
  const [gen, setGen] = useState(0);               // bumps on every navigation → remounts the slide
  const [dir, setDir] = useState('fwd');
  const [outgoing, setOutgoing] = useState(null);  // { idx, gen } of the slide animating out
  const [paused, setPaused] = useState(false);
  const [demo, setDemo] = useState(DEMO0);
  const [phase2, setPhase2State] = useState('pa'); // slide 2: pa | pa-out | pb
  const phase2Ref = useRef('pa');
  const setPhase2 = useCallback((p) => { phase2Ref.current = p; setPhase2State(p); }, []);
  const bokeh = useMemo(makeBokeh, []);
  const fit = useMemo(() => fitScene(stageHeight, SCENE), [stageHeight]);

  const { set, clear } = useTimers();
  const slideRef = useRef(-1);
  const genRef = useRef(0);
  const autoRef = useRef(0);
  const demoRef = useRef(0);
  const phaseRef = useRef(0);
  const clock = useRef({ start: 0, left: 0, holding: false });
  const ended = useRef(false);
  const goSlideRef = useRef(() => {});

  /* auto-advance; the last slide never advances on its own */
  const armTimer = useCallback((ms) => {
    clear(autoRef.current);
    clock.current.left = ms;
    clock.current.start = Date.now();
    if (slideRef.current < LAST) {
      autoRef.current = set(() => goSlideRef.current(slideRef.current + 1), ms);
    }
  }, [set, clear]);

  /* slide 1: fill the demo track one Gem at a time */
  const runDemo = useCallback(() => {
    setDemo(DEMO0);
    let k = 0;
    const step = () => {
      if (slideRef.current !== 0 || k >= 4) return;
      k += 1;
      const kk = k;
      setDemo((d) => ({ step: kk, line: kk - 1, pop: kk, chest: d.chest, flip: d.flip + 1 }));
      set(() => setDemo((d) => (d.pop === kk ? { ...d, pop: 0 } : d)), 600);
      if (kk === 4) {
        set(() => {
          setDemo((d) => ({ ...d, chest: true }));
          set(() => setDemo((d) => ({ ...d, chest: false })), 950);
        }, 400);
      } else {
        demoRef.current = set(step, 950);
      }
    };
    demoRef.current = set(step, 500);
  }, [set]);

  const goSlide = useCallback((n) => {
    clear(autoRef.current);
    clear(demoRef.current);
    clear(phaseRef.current);
    clock.current.holding = false;
    setPaused(false);

    const prev = slideRef.current;
    const prevGen = genRef.current;
    genRef.current = prevGen + 1;
    slideRef.current = n;
    setDir(n < prev ? 'back' : 'fwd');
    if (prev >= 0) {
      /* the leaving slide keeps the phase it was showing while it blurs out */
      setOutgoing({ idx: prev, gen: prevGen, phase: phase2Ref.current });
      set(() => setOutgoing((o) => (o && o.gen === prevGen ? null : o)), OUT_MS);
    }
    setGen(genRef.current);
    setSlide(n);

    if (n === 0) { setDemo(DEMO0); demoRef.current = set(runDemo, 1500); }
    setPhase2('pa');
    if (n === 1) {
      phaseRef.current = set(() => {
        setPhase2('pa-out');
        phaseRef.current = set(() => setPhase2('pb'), PHASE_OUT_MS);
      }, PHASE_A_MS);
    }
    armTimer(DUR);
  }, [set, clear, runDemo, armTimer, setPhase2]);
  useEffect(() => { goSlideRef.current = goSlide; }, [goSlide]);

  /* the title card plays first, then slide 1 starts */
  const introRef = useRef(0);
  const startSlides = useCallback(() => {
    clear(introRef.current);
    setIntro(false);
    goSlideRef.current(0);
  }, [clear]);
  useEffect(() => {
    introRef.current = set(startSlides, INTRO_MS);
    return () => clear(introRef.current);
  }, [set, clear, startSlides]);

  const pause = useCallback(() => {
    if (slideRef.current < 0 || slideRef.current >= LAST || clock.current.holding) return;
    clock.current.holding = true;
    setPaused(true);
    clear(autoRef.current);
    clock.current.left -= Date.now() - clock.current.start;
  }, [clear]);

  const resume = useCallback(() => {
    if (!clock.current.holding) return;
    clock.current.holding = false;
    setPaused(false);
    armTimer(Math.max(clock.current.left, 400));
  }, [armTimer]);

  const end = useCallback(() => {
    if (ended.current) return;
    ended.current = true;
    clear(introRef.current);
    clear(autoRef.current);
    clear(demoRef.current);
    clear(phaseRef.current);
    onEnd();
  }, [clear, onEnd]);

  /* a tap during the title card skips straight to slide 1 */
  const next = useCallback(() => {
    if (slideRef.current < 0) startSlides();
    else if (slideRef.current < LAST) goSlide(slideRef.current + 1);
    else end();
  }, [startSlides, goSlide, end]);
  const prev = useCallback(() => {
    if (slideRef.current < 0) startSlides();
    else goSlide(slideRef.current > 0 ? slideRef.current - 1 : 0);
  }, [startSlides, goSlide]);

  const renderSlide = (idx) => {
    if (idx === 0) return <SlideStart demo={demo} scene={fit.style} />;
    return (
      <SlideChest
        drawLabel={drawLabel}
        startsLabel={startsLabel}
        ctaLabel={ctaLabel}
        scene={fit.style}
        onCta={end}
      />
    );
  };

  /* the active slide plus, for OUT_MS, the one leaving; kept in design order */
  const layers = slide >= 0 ? [{ idx: slide, gen, state: 'gq-on', phase: phase2 }] : [];
  if (outgoing) layers.push({ idx: outgoing.idx, gen: outgoing.gen, state: 'gq-out', phase: outgoing.phase });
  layers.sort((a, b) => a.idx - b.idx || a.gen - b.gen);

  return (
    <div className="gq-overlay" onContextMenu={(e) => e.preventDefault()}>
      <div
        className={`gq-story${paused ? ' gq-paused' : ''}`}
        data-dir={dir}
        style={{ zoom, height: stageHeight }}
      >
        <BgRot />
        <div className="gq-sbg">
          <div className="gq-bggem gq-bgg1"><img src={gem} alt="" /></div>
          <div className="gq-bggem gq-bgg2"><img src={gem} alt="" /></div>
          <div className="gq-bggem gq-bgg3"><img src={gem} alt="" /></div>
          <div className="gq-bokeh">
            {bokeh.map((b) => <i key={b.id} style={b.style} />)}
          </div>
          <div className="gq-vignette" />
        </div>

        <div className="gq-bars" key={gen}>
          {[0, 1].map((k) => (
            <i key={k} className={k < slide ? 'gq-done' : (k === slide ? 'gq-cur' : '')} style={{ '--dur': `${DUR}ms` }} />
          ))}
        </div>

        <div className="gq-sh">
          <div className="gq-av"><div className="gq-avc"><img src={logo} alt="" /></div></div>
          <div className="gq-who"><b>GoSats</b><span>The Golden Quest</span></div>
          <button type="button" className="gq-x" onClick={end} aria-label="Close">
            <svg viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" /></svg>
          </button>
        </div>

        <TapZone side="l" onTap={prev} onHold={pause} onRelease={resume} />
        <TapZone side="r" onTap={next} onHold={pause} onRelease={resume} />

        {intro && <Intro stageHeight={stageHeight} />}

        {layers.map((l) => (
          <div
            key={`${l.idx}-${l.gen}`}
            className={`gq-slide gq-s${l.idx + 1} ${l.state}${l.idx === 1 ? ` ${PHASE_CLASS[l.phase]}` : ''}`}
          >
            {renderSlide(l.idx)}
          </div>
        ))}
      </div>
    </div>
  );
}
